import { environment } from "../../configuration";

const VOICE_ID = process.env.REACT_APP_ELEVENLABS_VOICE_ID || "";
const API_KEY = process.env.REACT_APP_ELEVENLABS_API_KEY || "";
const MODEL_ID = "eleven_turbo_v2_5";

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const fetchWithRetry = async (
  url: string,
  options: RequestInit,
  retries = 3,
): Promise<Response> => {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await fetch(url, options);
    } catch (err) {
      if (attempt === retries) throw err;
      await sleep(500 * Math.pow(2, attempt));
    }
  }
  throw new Error("Unreachable");
};

const uploadAudioBlob = async (audioBlob: Blob): Promise<string> => {
  const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.mp3`;
  const file = new File([audioBlob], fileName, { type: "audio/mpeg" });
  const formData = new FormData();
  formData.append("file", file);
  formData.append("folder", "game-quiz/tts");

  const response = await fetch(`${environment.apiUrl}/storage/upload`, {
    method: "POST",
    body: formData,
  });
  if (!response.ok) {
    throw new Error(`Error al subir audio (${response.status})`);
  }
  const data = await response.json();
  if (!data?.url) throw new Error("Respuesta de upload sin URL");
  return data.url as string;
};

const callElevenLabs = async (body: Record<string, unknown>): Promise<Blob> => {
  if (!API_KEY || !VOICE_ID) {
    throw new Error(
      "Falta configurar REACT_APP_ELEVENLABS_API_KEY o REACT_APP_ELEVENLABS_VOICE_ID",
    );
  }
  const response = await fetchWithRetry(
    `https://api.elevenlabs.io/v1/text-to-speech/${VOICE_ID}`,
    {
      method: "POST",
      headers: {
        "xi-api-key": API_KEY,
        "Content-Type": "application/json",
        Accept: "audio/mpeg",
      },
      body: JSON.stringify(body),
    },
  );
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`ElevenLabs error ${response.status}: ${errorText}`);
  }
  return response.blob();
};

export const elevenLabsService = {
  generateAudio: (text: string) =>
    callElevenLabs({
      text,
      model_id: MODEL_ID,
      language_code: "es",
      voice_settings: { stability: 0.5, similarity_boost: 0.75 },
    }),

  generateAndUploadAudio: async (text: string): Promise<string> => {
    const audioBlob = await elevenLabsService.generateAudio(text);
    return uploadAudioBlob(audioBlob);
  },

  generateAudioWithBlanks: (text: string) => {
    const ssml = `<speak>${text.replaceAll("{}", '<break time="1.5s"/>')}</speak>`;
    return callElevenLabs({
      text: ssml,
      model_id: MODEL_ID,
      enable_ssml_parsing: true,
      language_code: "es",
      voice_settings: { stability: 0.5, similarity_boost: 0.75 },
    });
  },

  generateAndUploadAudioWithBlanks: async (text: string): Promise<string> => {
    const audioBlob = await elevenLabsService.generateAudioWithBlanks(text);
    return uploadAudioBlob(audioBlob);
  },
};
